import { generateMap } from "~/utils/belt";
import type { GetTournamentQuery, GetTournamentsQuery } from "~/@graphql/types";

export type TournamentStatusType =
  | "upcoming"
  | "registering"
  | "ongoing"
  | "complete";

export type PlatformType = "mobile" | "pc" | "console" | "cross-platform" | "unknown";

function normalizePlatform(value: unknown): PlatformType {
  const platform = typeof value === "string" ? value.toLowerCase() : undefined;
  switch (platform) {
    case "mobile":
    case "pc":
    case "console":
    case "cross-platform":
      return platform;
    default:
      return "unknown";
  }
}

export type TournamentProps = {
  id?: string;
  compact?: boolean;
  name: string;
  description?: string | null;
  game: string;
  gameIcon: string;
  gameDetails: Path<GetTournamentQuery["tournament"], ["game"]>;
  posterURL: string;
  tournamentType: string;
  participantType: "team" | "individual";
  participants: Path<GetTournamentQuery["tournament"], ["participants"]>;
  teamType: string;
  teamTypeValue: string;
  teamSize?: number;
  platform: PlatformType;
  sponsor?: string;
  sponsors?: {
    id: any;
    name: string;
    nickname: string | null | undefined;
  }[];
  intendedParticipants?: number;
  registeredParticipants?: number;
  matchesPlayed?: number;
  results: Path<GetTournamentQuery["tournament"], ["results"]>;
  matchupsEmbed?: string;
  registrationFeeCurrency: string;
  registrationFee: string;
  dates: {
    tournamentDates: {
      start: Date;
      end: Date;
    };
    registrationPeriod: {
      start: Date;
      end: Date;
    };
  };
  rewardsData?: {
    rewards?: {
      [key: string]: string;
    }[];
    prizes?: {
      title?: string | null;
      type: string;
      name: string;
      value?: string;
    }[];
    placingPoints?: {
      [key in "stageName" | "matchPoints" | "bonusPoints"]: {
        [key: string]: string;
      };
    }[];
    bonusPoints?: { [key: string]: string }[];
  };
  tournament_stages?: Path<
    GetTournamentQuery["tournament"],
    ["tournament_stages"]
  >;
  recordedStats: string[];
  termsAndConditions?: string;
};

const parsePlacingPoints = (
  d?: string,
  stages?: Path<GetTournamentQuery["tournament"], ["tournament_stages"]>
) => {
  let result: {
    [key in "stageName" | "matchPoints" | "bonusPoints"]: {
      [key: string]: string;
    };
  }[] = [];
  if (d) {
    try {
      const parsed = JSON.parse(d);
      // if (parsed.placing) {
      //   result.push(
      //     Object.keys(parsed.placing).map((position) => ({
      //       [`#${position}`]: parsed.placing[position].replace("_PT", ""),
      //     }))
      //   );
      // }
      // if (parsed.rewards) {
      //   result.push(
      //     Object.keys(parsed.rewards).map((position) => ({
      //       [`#${position}`]: parsed.placing[position].replace("_PT", ""),
      //     }))
      //   );
      // }
      stages?.forEach((stage, index) => {
        if (parsed[index + 1]) {
          const stageName = stage.name;
          result.push({ ...parsed[index + 1], stageName });
        }
      });
    } catch (err) {
      console.error(err);
      return [];
    }
  }

  return result;
};

const prepareSingleTournieData = (
  tournament?: GetTournamentQuery["tournament"]
): TournamentProps => {
  let participantType: "team" | "individual";
  if (generateMap(tournament?.tournament_details ?? []).teamType) {
    participantType =
      generateMap(tournament?.tournament_details!)
        .teamType.toLowerCase()
        .includes("solo") ??
      generateMap(tournament?.tournament_details!)
        .teamType.toLowerCase()
        .includes("individual")
        ? "individual"
        : "team";
  } else {
    participantType = "team";
  }

  const teamSizeMap = {
    TEAM_SOLO: 1,
    TEAM_DUOS: 2,
    TEAM_TRIOS: 3,
    TEAM_3v3: 3,
    TEAM_SQUAD: 4,
    TEAM_5v5: 5,
  };

  return {
    id: tournament?.id,
    name: tournament?.name!,
    description: tournament?.description,
    tournamentType: tournament?.type.name!,
    participantType,
    participants: tournament?.participants ?? [],
    termsAndConditions: generateMap(tournament?.tournament_details ?? [])
      .termsAndConditions,
    teamType: generateMap(tournament?.tournament_details!).teamType
      ? generateMap(tournament?.tournament_details!)
          .teamType.toLowerCase()
          .replace("team_", "")
      : "",
    teamTypeValue: generateMap(tournament?.tournament_details!).teamType
      ? generateMap(tournament?.tournament_details!).teamType
      : "",
    teamSize:
      // @ts-expect-error
      teamSizeMap[generateMap(tournament?.tournament_details!).teamType],
    intendedParticipants: generateMap(tournament?.tournament_details ?? [])
      .maximumParticipants,
    posterURL: generateMap(tournament?.tournament_details ?? []).posterURL,
    platform: normalizePlatform(
      generateMap(tournament?.tournament_details ?? []).platform
    ),
    gameIcon: tournament?.game.poster_url!,
    game: tournament?.game.name!,
    gameDetails: tournament?.game!,
    recordedStats: tournament?.game.metadata
      ? tournament?.game.metadata.statsToRecord
      : [],
    sponsors: tournament?.sponsor.map((sponsor) => ({
      id: sponsor.party.id,
      name: sponsor.party.name,
      nickname: sponsor.party.nickname,
    })),
    dates: {
      tournamentDates: {
        start: new Date(
          generateMap(tournament?.tournament_details ?? []).tournamentStartTime
        ),
        end: new Date(
          generateMap(tournament?.tournament_details ?? []).tournamentEndTime
        ),
      },
      registrationPeriod: {
        start: new Date(
          generateMap(tournament?.tournament_details ?? []).registrationStart
        ),
        end: new Date(
          generateMap(tournament?.tournament_details ?? []).registrationStop
        ),
      },
    },
    registeredParticipants: tournament?.participants.length,
    results: tournament?.results ?? [],
    matchesPlayed: tournament?.results ? tournament.results.length : 0,
    matchupsEmbed: generateMap(tournament?.tournament_details ?? [])
      .matchupsEmbed,
    registrationFee: generateMap(tournament?.tournament_details ?? [])
      .registrationFee,
    registrationFeeCurrency:
      generateMap(tournament?.tournament_details ?? [])
        .registrationFeeCurrency ?? "KES",
    rewardsData: {
      prizes: tournament?.tournament_rewards
        .filter((reward) => reward.type.value === "PRIZE")
        .map((reward) => ({
          value: reward.value,
          name: reward.reward.name,
          type: reward.reward.type.value!,
        })),
      rewards: tournament?.tournament_rewards
        .filter((reward) => reward.type.value! !== "PRIZE")
        .map((reward) => ({
          [`${reward.reward.name} (${reward.type.name})`]: reward.value,
        })),
      placingPoints: parsePlacingPoints(
        generateMap(tournament?.tournament_details ?? []).rules,
        tournament?.tournament_stages
      ),
      // bonusPoints: [{}],
    },
    tournament_stages: tournament?.tournament_stages ?? []
  };
};

export const prepareManyTournamentsData = ({
  tournaments,
}: {
  tournaments: GetTournamentsQuery["tournaments"];
}) => {
  // @ts-expect-error
  return tournaments.map((tournament) => prepareSingleTournieData(tournament));
};

export const getTournamentStatus = ({
  registrationPeriod,
  registeredParticipants,
  matchesPlayed,
  tournamentDates,
}: {
  registrationPeriod: {
    start: Date;
    end: Date;
  };
  tournamentDates: {
    start: Date;
    end: Date;
  };
  registeredParticipants?: number;
  matchesPlayed?: number;
}) => {
  let tournamentStatus: TournamentStatusType = "upcoming";
  let statusFact;
  if (
    new Date() < registrationPeriod.start
  ) {
    tournamentStatus = "upcoming";
    statusFact = 0;
  } else if (
    new Date() >= registrationPeriod.start &&
    new Date() <= registrationPeriod.end
  ) {
    tournamentStatus = "registering";
    statusFact = registeredParticipants;
  } else {
    if (tournamentDates.end < new Date()) {
      statusFact = undefined;
      tournamentStatus = "complete";
    }
    if (tournamentDates.end >= new Date()) {
      tournamentStatus = "ongoing";
      statusFact = matchesPlayed;
    }
  }

  return {
    statusFact,
    tournamentStatus,
  };
};

export default prepareSingleTournieData;
